<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Projections';
    let nextLink= '/Documentation/Defining_an_Editor/Ease_of_Editing';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="projections-and-indentation-1"
  bind:intersecting={visibleById["projections-and-indentation-1"]}
  onIntersectChange={(visible) => setVisible("projections-and-indentation-1", visible)}
>Projections and Indentation</SectionComponent>
<p>Indentation in a projection directly affects its display. Thus, in the following example <code>literal text</code> will
appear eight spaces farther to the right than <code>This is</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-indentation.edit#L6-L14

Text &#123;
[
This is
        literal text
      that is projected in the
                editor
  for every concept of type Text.
]
&#125;</code></pre>
<p>However, this is not the complete story. Any whitespace before the line with the least indentation is ignored.
Therefore, you can indent the projection block as a whole without affecting its appearance.
The next example produces the exact same result as the previous.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-indentation.edit#L16-L24

Text2 &#123;
            [
            This is
                    literal text
                  that is projected in the
                            editor
              for every concept of type Text.
            ]
&#125;</code></pre>
<p>In the following example, the line with the least indentation is
<code>for every concept of type Text.</code>
It will not be indented. On the other hand,
the text <code>This is</code> will be indented 10 spaces, <code>literal text</code> will be indented 8 spaces, etc.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-indentation.edit#L26-L34

Text3 &#123;
    [
                 This is
               literal text
          that is projected in the
                  editor
       for every concept of type Text.
            ]
&#125;</code></pre>
<p>When determining of the least indent, the indentation of the closing bracket is also considered. Therefore,
the following example will take the closing bracket as margin, and even the line <code>for every concept of type Text.</code>
will be indented with three spaces.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-indentation.edit#L37-L45

    [
                 This is
               literal text
          that is projected in the
                  editor
       for every concept of type Text.
    ]
&#125;
</code></pre>
<Note> 
{#snippet header()} Indentation is incremental{/snippet}
{#snippet content()}
Any indentation applied to a property is added to the indentation of that property’s projection.
Thus, when a `Text` element is used within another projection,
the indentation that is defined by the projection in the definition for `Text` will be preceded
by any indentation defined in the projection for the enclosing concept.
{/snippet}
</Note>


<PrevNextSection {prevLink} {nextLink} />